<template>
    <div class="DdrRequirements">
        <div class="DdrRequirements__list">
            <RequirementAccordion
                v-for="requirementData in requirementsData"
                :key="requirementData.requirement"
                :ref="requirementData.requirement"
                :requirement-type="requirementData.requirement"
                :title="getRequirementTitle(requirementData.requirement)"
                :expanded="expandedMap[requirementData.requirement] || false"
                @update:expanded="toggleExpansion(requirementData.requirement, $event)"
            >
                <template #content>
                    <div class="DdrRequirements__content">
                        <DdrDynamicForm
                            class="DdrRequirementsForm"
                            :form-id="formsIdsByRequirementType[requirementData.requirement]"
                            :rbac-disabled="rbacDisabled"
                        />
                    </div>
                </template>
                <template #beforeExpandToggle>
                    <CheckboxInput
                        label="Not Found"
                        :disabled="rbacDisabled"
                        :name="`${requirementData.requirement}-not-found`"
                        :model-value="requirementNotFoundMap[requirementData.requirement] || false"
                        data-stop-propagation
                        @change="toggleRequirementNotFound($event, requirementData)"
                    />
                </template>
            </RequirementAccordion>
        </div>
    </div>
</template>

<style lang="scss">
.DdrRequirements {
    height: 100%;
    display: flex;
    flex-direction: column;

    &__list {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .CheckboxInput {
        &__field {
            width: 16px;
            height: 16px;
        }

        &__text {
            font-size: 14px;
            font-weight: normal;
        }
    }

    .RequirementAccordion__header {
        background-color: var(--eidWhite);
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import mapValues from 'lodash/mapValues';

    import { DocumentDataReviewRequirementInstance } from '@evidentid/tprm-portal-lib/models/document-data-review';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import RequirementAccordion from '@/components/requirement-acoordion/RequirementAccordion.vue';
    import DdrDynamicForm from '@/modules/document-data-review/components/commons/ddr-dynamic-form/DdrDynamicForm.vue';
    import { useDocumentDataReviewStore } from '@/modules/document-data-review/store';
    import { performBatchNotFoundToggle } from '@/modules/document-data-review/utils/perform-default-not-found-toggle/performDefaultNotFoundToggle';
    import { buildRequirementFormId, getDdrIdFromFormId, getRequirementTypeFromFormId } from '@/modules/document-data-review/utils/requirement-form-id-utils/requirementFormIdUtils';
    import CheckboxInput from '@/modules/dynamic-form/components/style-inputs/checkbox-input/CheckboxInput.vue';
    import { FieldPath } from '@/modules/dynamic-form/models/FieldPath.model';
    import { useDynamicFormStore } from '@/modules/dynamic-form/store';
    import { createCompoundUnifiedFieldRecordValue } from '@/modules/dynamic-form/utils/form-field-helpers/formFieldHelpers';
    import defaultCoverageIcon from '@/modules/entity-details/assets/coverage-icon-default.svg';
    import { getRequirementTypeIconUri } from '@/modules/entity-details/utils/getRequirementTypeIconUri';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';

    import { initForms } from './init-forms';

    export default defineComponent({
        name: 'DdrRequirements',
        components: {
            DdrDynamicForm,
            RequirementAccordion,
            CheckboxInput,
        },
        props: {
            ddrId: {
                type: String as PropType<string>,
                required: true,
            },
            requirementsData: {
                type: Array as PropType<DocumentDataReviewRequirementInstance[]>,
                default: () => ([] as DocumentDataReviewRequirementInstance[]),
            },
            requirementsSchema: {
                type: Object as PropType<Record<TprmRequirementType, any>>,
                default: (): Record<TprmRequirementType, any> => ({}),
            },
            rbacDisabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        data() {
            return {
                expandedMap: {} as Record<TprmRequirementType, boolean>,
                requirementNotFoundMap: {} as Record<TprmRequirementType, boolean>,
                defaultCoverageIcon,
            };
        },
        computed: {
            formsIdsByRequirementType(): Record<TprmRequirementType, string> {
                const scope = this as any;
                return Object.keys(useDynamicFormStore().forms || {})
                    .filter((formId) => {
                        const ddrId = getDdrIdFromFormId(formId);
                        return ddrId === scope.ddrId;
                    })
                    .reduce((acc: Record<TprmRequirementType, string>, formId: string) => {
                        const requirementType = getRequirementTypeFromFormId(formId) as TprmRequirementType;
                        if (requirementType) {
                            acc[requirementType] = formId;
                        }
                        return acc;
                    }, {} as Record<TprmRequirementType, string>);
            },
        },
        created() {
            const scope = this as any;
            initForms({
                scope,
                ddrId: scope.ddrId,
                requirementsData: scope.requirementsData,
                requirementsSchema: scope.requirementsSchema,
                rbacDisabled: scope.rbacDisabled,
                requirementTypeComponentRef: {
                    ensureRequirementExpanded:
                        (requirementType: TprmRequirementType, callbackFn?: () => void) =>
                            scope.ensureRequirementExpanded(requirementType, callbackFn),
                } as Record<TprmRequirementType, any>,
            });
        },
        methods: {
            /**
             * Public method: Ensures the specified requirement panel is expanded.
             * Used by external components (e.g., DocumentDataReviewErrors) to expand panels
             * before scrolling to specific fields within them.
             *
             * @param requirementType - The requirement type to ensure is expanded
             * @returns true if expansion was triggered, false if already expanded
             */
            ensureRequirementExpanded(requirementType: TprmRequirementType): boolean {
                const scope = this as any;
                const requirementRef = scope.$refs[requirementType];
                if (requirementRef && !scope.expandedMap[requirementType]) {
                    this.toggleExpansion(requirementType, true);
                    return true;
                }
                // return expanding was triggered or not helps caller to determine whether need to wait for animation
                return false;
            },
            typeIconSrc(requirementType: TprmRequirementType) {
                return getRequirementTypeIconUri(requirementType) || defaultCoverageIcon;
            },
            getRequirementTitle(requirement: TprmRequirementType): string {
                return useDashboardStore().tprmRequirementsDefinitions[requirement]?.defaultLabel
                    || requirement;
            },
            toggleExpansion(requirement: TprmRequirementType, expanded: boolean | Event) {
                this.expandedMap = { ...this.expandedMap, [requirement]: expanded };
            },
            toggleRequirementNotFound(event: Event, requirementData: DocumentDataReviewRequirementInstance) {
                const isNotFound = (event.target as HTMLInputElement).checked;
                const formId = buildRequirementFormId(this.ddrId, requirementData.requirement);
                this.requirementNotFoundMap = {
                    ...this.requirementNotFoundMap,
                    [requirementData.requirement]: isNotFound,
                };
                useDocumentDataReviewStore().setDdrDisabledFields({
                    formId,
                    disabledFields: mapValues(
                        requirementData.fields,
                        (_, path) => createCompoundUnifiedFieldRecordValue<boolean>(
                            useDynamicFormStore().forms[formId]?.fieldsMeta?.[path as FieldPath]?.defaultValue,
                            isNotFound,
                        ),
                    ),
                });
                const fields = Object.keys(requirementData.fields).map((path: string) => ({
                    path: path as FieldPath,
                    currentValue: useDynamicFormStore().forms[formId]?.values?.[path as FieldPath],
                    fieldMeta: useDynamicFormStore().forms[formId]?.fieldsMeta?.[path as FieldPath],
                    beforeNotFoundValue:
                        useDocumentDataReviewStore().forms[formId]?.valuesBeforeNotFound?.[path as FieldPath],
                }));
                performBatchNotFoundToggle({
                    fields,
                    formId,
                    setToNotFound: isNotFound,
                }, {
                    setBatchBeforeValuesFunc: useDocumentDataReviewStore().setValuesBeforeNotFound,
                    setBatchValuesFunc: useDynamicFormStore().setValues,
                    setBatchDisabledFunc: useDynamicFormStore().setDisabledFields,
                });
            },
        },
    });
</script>
